<template>
  <div>
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="$store.state.currentUser.id" />
    <div class="home-page">
      <div class="breadcrumb">
        <span class="breadcrumb__item">{{ $i18nPresenter.t('Home') }}</span>
      </div>
      <h2>{{ $i18nPresenter.t('Welcome') }}, {{ fullName }}!</h2>
      <div class="border-b border-secondary-20"></div>
      <section class="action-buttons">
        <aha-button
          class="new-presentation-btn"
          size="xl"
          @click="onClickNewPresentation"
        >
          <aha-icon
            name="add"
            width="16"
            height="16"
            viewBox="0 0 24 24"
          />
          <span>{{ $i18nPresenter.t("New Presentation") }}</span>
        </aha-button>
        <aha-button
          class="import-btn"
          size="xl"
          variant="white"
          @click="openImportModal"
        >
          <aha-icon
            name="import"
            width="16"
            height="16"
            viewBox="0 0 24 25"
          />
          <span>{{ $i18nPresenter.t("Import") }}</span>
        </aha-button>
      </section>
      <section
        v-if="!isLoadingData"
        :class="[
          'content',
          {
            'has-sidebar-content': hasSidebarContent,
          },
        ]"
      >
        <section class="center-section">
          <PresentationsSection
            v-if="presentations && presentations.length"
            class="presentations-section"
            :presentations="presentations"
          >
            <template #before>
              <div class="flex justify-between">
                <h3>{{ $i18nPresenter.t('Recent') }}</h3>
                <aha-button-link
                  @click="toMyPresentation"
                >
                  <span>{{ $i18nPresenter.t('My Presentations') }}</span>
                  <aha-icon
                    name="chevron-right-2"
                    width="10"
                    height="16"
                    viewBox="0 0 8 14"
                  >
                  </aha-icon>
                </aha-button-link>
              </div>
            </template>
          </PresentationsSection>
          <TemplatesSection
            v-if="templates && templates.length"
            @browse-all-click="toTemplateLbrary"
            :templates="templates"
          >
            <template #before>
              <div class="flex justify-between">
                <h3>{{ $i18nPresenter.t('Popular Templates') }}</h3>
                <aha-button-link
                  @click="toTemplateLbrary"
                >
                  <span>{{ $i18nPresenter.t('Browse all') }}</span>
                  <aha-icon
                    name="chevron-right-2"
                    width="10"
                    height="16"
                    viewBox="0 0 8 14"
                  >
                  </aha-icon>
                </aha-button-link>
              </div>
            </template>
          </TemplatesSection>
          <FeedbacksSection
            v-if="feedbacks && feedbacks.length"
            @click-feedback="toReportPage"
            :feedbacks="feedbacks"
            class="feedbacks-section hide-desktop"
          >
            <template #before>
              <h3 class="p-4">
                {{ $i18nPresenter.t('From your audience') }}
              </h3>
            </template>
          </FeedbacksSection>
          <SocialMedia
            v-if="!isWhiteLabel"
            @click-channel="trackClickChannel"
            :channels="channels"
            class="hide-desktop"
          >
            <template #before>
              <h3>
                {{ $i18nPresenter.t('Join us on') }}
              </h3>
            </template>
          </SocialMedia>
          <NotificationsSection
            v-if="notifications && notifications.length && !isWhiteLabel"
            @view-more-click="trackViewMoreNotifications"
            @read-notification="trackReadNotification"
            :notifications="notifications"
          >
            <template #before>
              <h3 class="p-4 flex gap-4">
                <img src="@/assets/images/bell.svg" />
                {{ $i18nPresenter.t('Notifications') }}
              </h3>
            </template>
          </NotificationsSection>
        </section>
        <section class="sidebar-section hide-mobile">
          <FeedbacksSection
            v-if="feedbacks && feedbacks.length"
            :feedbacks="feedbacks"
            @click-feedback="toReportPage"
            class="feedbacks-section"
          >
            <template #before>
              <h3 class="p-4">
                {{ $i18nPresenter.t('From your audience') }}
              </h3>
            </template>
          </FeedbacksSection>
          <SocialMedia
            v-if="!isWhiteLabel"
            @click-channel="trackClickChannel"
            :channels="channels"
          >
            <template #before>
              <h3>
                {{ $i18nPresenter.t('Join us on') }}
              </h3>
            </template>
          </SocialMedia>
        </section>
      </section>
      <SkeletonContent
        v-if="isLoadingData"
        :class="[
          'content-skeleton',
          {
            'has-sidebar-content': hasSidebarContent,
          },
        ]"
      >
      </SkeletonContent>
      <ModalImport
        v-aha-emit-action.view
        v-if="showImportModal"
        @hide="showImportModal = false"
      />
      <TrialNotAvailableModal v-if="isShowTrialNotEligibleModal" @hide="hideTrialNotAvailableModalAction" />
      <EnjoyTrialModal v-if="isShowEnjoyTrialModal" @hide="hideEnjoyTrialModalAction" />
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import Utils from '@/util'
import { HOME, IMPORT } from '@/constant/eventActions'
import { ROUTE_NAMES, EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'

import ModalImport from '@/components-v2/modals/Import.vue'
import PresentationsSection from './components/PresentationsSection.vue'
import FeedbacksSection from './components/FeedbacksSection.vue'
import TemplatesSection from './components/TemplatesSection.vue'
import NotificationsSection from './components/NotificationsSection.vue'
import SocialMedia from './components/SocialMedia.vue'
import SkeletonContent from './components/SkeletonContent.vue'
import EnjoyTrialModal from '@/components-v2/modals/trial/EnjoyTrialModal.vue'
import TrialNotAvailableModal from '@/components-v2/modals/trial/TrialNotAvailableModal.vue'
import CreateNewPresentationMixin from '@/mixins/presentation/CreateNewPresentationMixin'
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'

import { socialMediaList } from '@/constant/home.constants'

export default {
  components: {
    ModalImport,
    PresentationsSection,
    FeedbacksSection,
    TemplatesSection,
    NotificationsSection,
    SocialMedia,
    SkeletonContent,
    TrialNotAvailableModal,
    EnjoyTrialModal,
    InvitationNotifications,
    PublicAnnouncement,
  },
  mixins: [CreateNewPresentationMixin],
  data() {
    return {
      showImportModal: false,
      channels: socialMediaList,
    }
  },
  computed: {
    ...mapGetters('homePage', ['presentations', 'feedbacks', 'notifications', 'templates', 'fullName', 'isLoadingData']),
    ...mapGetters('trialPlan', [
      'isShowTrialNotEligibleModal',
      'isShowEnjoyTrialModal',
    ]),
    pageEventCategory() {
      return EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name]
    },
    isWhiteLabel() {
      return Utils.checkWhiteLabelDomain()
    },
    hasSidebarContent() {
      const showSocialMedia = !this.isWhiteLabel
      const hasFeedbacks = this.feedbacks && this.feedbacks.length

      return showSocialMedia || hasFeedbacks
    },
  },
  async mounted() {
    await this.getHomePageDataAction()
  },
  methods: {
    ...mapActions('homePage', [
      'getHomePageDataAction',
    ]),
    ...mapActions('trialPlan', [
      'hideTrialNotAvailableModalAction',
      'hideEnjoyTrialModalAction',
    ]),
    onClickNewPresentation() {
      this.createNewPresentation('From home page')
    },
    trackOpenImportModal() {
      const eventAction = IMPORT.OPEN_MODAL
      const data = {
        eventAction,
        eventCategory: this.pageEventCategory,
      }
      Utils.trackGA4AndMixpanel(eventAction, data)
    },
    toMyPresentation() {
      this.trackToMyPresentation()
      this.$router.push({ name: ROUTE_NAMES.PRESENTATIONS })
    },
    trackToMyPresentation() {
      const eventAction = HOME.TO_MY_PRESENTATION_LIST
      const data = {
        eventAction,
        eventCategory: this.pageEventCategory,
      }
      Utils.trackGA4AndMixpanel(eventAction, data)
    },
    toTemplateLbrary() {
      this.trackBrowseAll()
      this.$router.push({ name: ROUTE_NAMES.TEMPLATE_LIBRARY, query: { from: 'home' } })
    },
    trackBrowseAll() {
      const eventAction = HOME.TO_TEMPLATE_LIBRARY
      const data = {
        eventAction,
        eventCategory: this.pageEventCategory,
      }
      Utils.trackGA4AndMixpanel(eventAction, data)
    },
    trackViewMoreNotifications() {
      const eventAction = HOME.SEE_MORE_NOTIFICATIONS
      const data = {
        eventAction,
        eventCategory: this.pageEventCategory,
      }
      Utils.trackGA4AndMixpanel(eventAction, data)
    },
    trackClickChannel(channelName) {
      const eventAction = HOME.JOIN_COMMUNITY
      const data = {
        eventAction,
        eventCategory: this.pageEventCategory,
        event_label: channelName,
      }
      Utils.trackGA4AndMixpanel(eventAction, data)
    },
    toReportPage(feedback) {
      this.trackClickFeedback(feedback.comment)
      this.$router.push(
        {
          name: ROUTE_NAMES.PRESENTATION_REPORT,
          params: { id: feedback.presentationId },
          query: { tab: 'feedback' },
        },
      )
    },
    trackClickFeedback(comment) {
      const eventAction = HOME.READ_FEEDBACK
      const data = {
        eventAction,
        eventCategory: this.pageEventCategory,
        event_label: comment,
      }
      Utils.trackGA4AndMixpanel(eventAction, data)
    },
    trackReadNotification(notification) {
      const eventAction = HOME.READ_NOTIFICATION
      const data = {
        eventAction,
        eventCategory: this.pageEventCategory,
        event_label: notification.title,
      }
      Utils.trackGA4AndMixpanel(eventAction, data)
    },
    openImportModal() {
      this.showImportModal = true
      this.trackOpenImportModal()
    },
  },
}
</script>

<style lang="scss" scoped>
.home-page {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 2.5rem 5rem 2.5rem;
    @media (max-width: 600px) {
        padding: 1rem 1rem 5rem 1rem;
    }

    & > h2 {
      font-size: 1.5rem;
    }

    & > .action-buttons {
      display: flex;
      gap: 1rem;

      @media (max-width: 600px) {
        & > .new-presentation-btn {
          flex-grow: 1;
          flex-shrink: 1;
          flex-basis: 15%;
        }

        & > .import-btn {
          flex: 1;
        }
      }
    }

    & > .content {
      display: grid;
      grid-template-columns: [main-start sidebar-start] minmax(0, 1fr) [sidebar-end main-end];
      grid-auto-rows: auto;
      gap: 1rem;

      @media (min-width: 1025px) {
        &.has-sidebar-content {
          grid-template-columns:
            [main-start] minmax(0, 1fr)
            [sidebar-start main-end] minmax(200px, 377px)
            [sidebar-end];
        }
      }

      & .hide-desktop {
        @media (min-width: 1025px) {
          display: none;
        }
      }

      & .hide-mobile {
        @media (max-width: 1024px) {
          display: none !important;
        }
      }

      & > .center-section {
        grid-column: main;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      & > .sidebar-section {
        grid-column: sidebar;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
    }
}
</style>
